<template>
  <div>
    <v-alert
      type="info"
      class="mb-6"
    >
      Outbound webhooks are signed (HMAC-SHA256, <code>X-August-Signature</code>) with a single active
      key at a time. Any change here — creating, enabling, disabling, or setting a primary key — may take
      up to 10 minutes to take effect while our cache refreshes. Until then, a disabled key stays valid, so
      keep both the old and new key signatures valid during that window before relying on the change.
    </v-alert>

    <div class="d-flex justify-end mb-4">
      <v-btn
        outlined
        height="48"
        @click="openCreateApiKey"
      >
        <v-icon
          small
          class="mr-2"
        >mdi-plus</v-icon>
        Create API key
      </v-btn>
    </div>

    <v-data-table
      :headers="apiKeyHeaders"
      :items="apiKeys"
      item-key="id"
      hide-default-footer
    >
      <template v-slot:item.apiKey="{ item }">
        <span style="white-space: nowrap; font-family: monospace;">{{ item.apiKey }}</span>
      </template>

      <template v-slot:item.status="{ item }">
        <div class="d-flex flex-column status-chips">
          <v-chip
            :color="item.disabled ? 'error' : 'success'"
            label
            small
          >{{ item.disabled ? 'Disabled' : 'Enabled' }}</v-chip>
          <v-chip
            v-if="item.isPrimary"
            color="info"
            label
            small
            class="mt-1"
          >Primary key</v-chip>
        </div>
      </template>

      <template v-slot:item.createdAt="{ item }">
        {{ item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—' }}
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center">
          <v-tooltip
            v-if="item.isPrimary"
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-switch
                  :input-value="!item.disabled"
                  :label="item.disabled ? 'Disabled' : 'Enabled'"
                  dense
                  hide-details
                  disabled
                  class="mt-0 key-switch"
                ></v-switch>
              </span>
            </template>
            <span>Promote another key to Primary key before disabling this one.</span>
          </v-tooltip>
          <v-switch
            v-else
            :input-value="!item.disabled"
            :label="item.disabled ? 'Disabled' : 'Enabled'"
            @change="toggleApiKey(item)"
            dense
            hide-details
            class="mt-0 key-switch"
          ></v-switch>
          <v-btn
            v-if="!item.isPrimary && !item.disabled"
            text
            small
            class="ml-4"
            @click="promoteApiKey(item)"
          >Set as primary key</v-btn>
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialogs.createKey"
      width="600"
      height="300"
    >
      <v-card>
        <v-card-title class="pa-8 justify-center">
          <v-icon class="black--text">mdi-key-plus</v-icon>
        </v-card-title>
        <v-card-text class="pb-8 grey--text text--darken-4">
          <p class="mb-8">Create a new API key for <strong>{{credential.fields.name.value}}</strong>? It will inherit the same scopes as this credential's existing key.</p>

          <v-btn
            width="187"
            height="48"
            class="mr-5"
            dark
            :loading="isSubmitting"
            :disabled="isSubmitting"
            @click="createApiKeyConfirm"
          >Create key</v-btn>
          <v-btn
            width="187"
            height="48"
            outlined
            :disabled="isSubmitting"
            @click="dialogs.createKey = false"
          >Cancel</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogs.newKeyReveal"
      width="600"
      height="300"
      persistent
    >
      <v-card>
        <v-card-title class="pa-8">
          <h1 class="mb-0">New API key</h1>
        </v-card-title>
        <v-card-text class="px-8 pb-8 grey--text text--darken-4">
          <p class="mb-4">Copy this key now — it won't be shown again.</p>
          <v-text-field
            readonly
            outlined
            height="46"
            dense
            class="text-field-outlined-overrides"
            append-icon="mdi-content-copy"
            @click:append="clickCopy(newApiKey && newApiKey.apiKey)"
            :value="newApiKey && newApiKey.apiKey"
          ></v-text-field>

          <v-btn
            width="187"
            height="48"
            dark
            @click="closeNewKeyReveal"
          >Done</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogs.toggleKey"
      width="600"
      height="300"
    >
      <v-card>
        <v-card-title class="pa-8 justify-center">
          <v-icon class="black--text">mdi-alert-circle</v-icon>
        </v-card-title>
        <v-card-text class="pb-8 grey--text text--darken-4">
          <p class="mb-8">
            Are you sure you want to
            <strong>{{ keyPendingAction && keyPendingAction.disabled ? 'enable' : 'disable' }}</strong>
            the key <strong>{{ keyPendingAction && keyPendingAction.apiKey }}</strong>?
          </p>

          <ul class="mb-8">
            <li>It may take up to 10 minutes for this change to take effect while our cache refreshes.</li>
            <li v-if="keyPendingAction && !keyPendingAction.disabled">A disabled key stays valid during that time, so keep its signature valid until then.</li>
          </ul>

          <v-btn
            width="187"
            height="48"
            class="mr-5"
            dark
            :loading="isSubmitting"
            :disabled="isSubmitting"
            @click="toggleApiKeyConfirm"
          >Confirm</v-btn>
          <v-btn
            width="187"
            height="48"
            outlined
            :disabled="isSubmitting"
            @click="dialogs.toggleKey = false"
          >Cancel</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogs.promoteKey"
      width="600"
      height="300"
    >
      <v-card>
        <v-card-title class="pa-8 justify-center">
          <v-icon class="black--text">mdi-alert-circle</v-icon>
        </v-card-title>
        <v-card-text class="pb-8 grey--text text--darken-4">
          <p class="mb-8">
            Set the key <strong>{{ keyPendingAction && keyPendingAction.apiKey }}</strong> as the primary key?
          </p>

          <ul class="mb-8">
            <li>It may take up to 10 minutes for webhooks to be signed with the new primary key.</li>
            <li>Keep both the old and new key signatures valid during this time.</li>
          </ul>

          <v-btn
            width="187"
            height="48"
            class="mr-5"
            dark
            :loading="isSubmitting"
            :disabled="isSubmitting"
            @click="promoteApiKeyConfirm"
          >Set as primary key</v-btn>
          <v-btn
            width="187"
            height="48"
            outlined
            :disabled="isSubmitting"
            @click="dialogs.promoteKey = false"
          >Cancel</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import util from '../util';

export default {
  name: 'CredentialApiKeysTab',
  props: [
    'credential',
  ],
  data() {
    return {
      apiKeys: [],
      dialogs: {
        createKey: false,
        newKeyReveal: false,
        toggleKey: false,
        promoteKey: false,
      },
      keyPendingAction: null,
      newApiKey: null,
      isSubmitting: false,
      apiKeyHeaders: [
        { text: 'Key', value: 'apiKey', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Created', value: 'createdAt' },
        { text: '', value: 'actions', sortable: false },
      ],
    };
  },
  created: function () {
    this.fetchApiKeys();
  },
  methods: {
    clickCopy: util.clickCopy,
    apiKeysUrl: function () {
      return `${this.$store.state.apiHost}/credentials/${this.credential.fields.clientID.value}/keys`;
    },
    fetchApiKeys: async function () {
      try {
        const result = await util.axios({url: this.apiKeysUrl(), method: 'get'});
        this.apiKeys = result.data;
      } catch (error) {
        this.$store.commit('snackbar', {text: 'Failed to load API keys', color: 'error'});
      }
    },
    openCreateApiKey: function () {
      this.dialogs.createKey = true;
    },
    closeNewKeyReveal: function () {
      this.dialogs.newKeyReveal = false;
      // Drop the one-time-reveal secret from memory once the user is done with it.
      this.newApiKey = null;
    },
    createApiKeyConfirm: async function () {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        const result = await util.axios({url: this.apiKeysUrl(), method: 'post'});

        this.newApiKey = result.data;
        this.apiKeys.push(result.data);
        this.dialogs.createKey = false;
        this.dialogs.newKeyReveal = true;
        this.$store.commit('snackbar', {text: 'API key created', color: 'success'});
      } catch (error) {
        this.$store.commit('snackbar', {text: 'Failed to create API key', color: 'error'});
      } finally {
        this.isSubmitting = false;
      }
    },
    toggleApiKey: function (key) {
      this.keyPendingAction = key;
      this.dialogs.toggleKey = true;
    },
    toggleApiKeyConfirm: async function () {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      const key = this.keyPendingAction;
      const disabled = !key.disabled;

      try {
        const result = await util.axios({
          url: `${this.apiKeysUrl()}/${key.id}`,
          method: 'put',
          data: {disabled},
        });

        key.disabled = result.data.disabled;
        this.dialogs.toggleKey = false;
        this.$store.commit('snackbar', {text: disabled ? 'API key disabled' : 'API key enabled', color: 'success'});
      } catch (error) {
        this.$store.commit('snackbar', {text: 'Failed to update API key', color: 'error'});
      } finally {
        this.isSubmitting = false;
      }
    },
    promoteApiKey: function (key) {
      this.keyPendingAction = key;
      this.dialogs.promoteKey = true;
    },
    promoteApiKeyConfirm: async function () {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      const key = this.keyPendingAction;

      try {
        await util.axios({
          url: `${this.apiKeysUrl()}/${key.id}/promote`,
          method: 'post',
        });

        for (const apiKey of this.apiKeys) {
          this.$set(apiKey, 'isPrimary', apiKey.id === key.id);
        }
        this.dialogs.promoteKey = false;
        this.$store.commit('snackbar', {text: 'API key set as primary key', color: 'success'});
      } catch (error) {
        this.$store.commit('snackbar', {text: 'Failed to set primary key', color: 'error'});
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.status-chips {
  width: 100%;
  padding: 8px 0;
}
.status-chips ::v-deep .v-chip {
  width: 100%;
  justify-content: center;
}

/* Trim the switch down and vertically center it with the row's text/button. */
.key-switch {
  padding-top: 0;
  flex: 0 0 auto;
}
.key-switch ::v-deep .v-input__slot {
  margin-bottom: 0;
}
</style>
